import { useCallback, useState } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Grid,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Table,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
  Button,
} from '@chakra-ui/react'
import {
  X,
  Trash2,
  ChevronUp,
  ChevronDown,
  CalendarClock,
  CheckCircle,
  FileText,
  Wallet,
  TrendingUp,
  MoreVertical,
  Plus,
  Edit,
  Vote,
} from 'lucide-react'
import { useBudget, useBudgetLines, useBudgets, useDeleteBudget, useVoteBudget, useDeleteBudgetLine } from '../hooks'
import { BudgetLineModal } from './BudgetLineModal'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { BudgetStatus, BudgetLine } from '../types'
import { BUDGET_TYPE_LABELS } from '../types'

interface BudgetDetailDrawerProps {
  budgetId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function BudgetDetailDrawer({
  budgetId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: BudgetDetailDrawerProps) {
  const { data: budget, isLoading, error } = useBudget(budgetId ?? undefined)
  const { data: budgetLines } = useBudgetLines(budgetId ?? undefined)
  const { data: allBudgets } = useBudgets(budget?.residence_id, budget?.fiscal_year_id)
  const deleteBudget = useDeleteBudget()
  const voteBudget = useVoteBudget()
  const deleteBudgetLine = useDeleteBudgetLine()

  // Calculer le montant total des budgets charges courantes pour valider le 5% ALUR
  const getOperatingBudgetTotal = () => {
    if (!allBudgets || !budget) return 0
    return allBudgets
      .filter(b => b.budget_type === 'operating' && b.id !== budget.id)
      .reduce((sum, b) => sum + (b.total_amount || 0), 0)
  }

  const operatingTotal = getOperatingBudgetTotal()
  const minimumALUR = operatingTotal * 0.05

  // Budget Line Modal state
  const [budgetLineModalOpen, setBudgetLineModalOpen] = useState(false)
  const [editingBudgetLine, setEditingBudgetLine] = useState<BudgetLine | null>(null)

  // Confirmation dialogs state
  const [showVoteConfirm, setShowVoteConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleOpenAddLine = () => {
    setEditingBudgetLine(null)
    setBudgetLineModalOpen(true)
  }

  const handleOpenEditLine = (line: BudgetLine) => {
    setEditingBudgetLine(line)
    setBudgetLineModalOpen(true)
  }

  const handleCloseLineModal = () => {
    setBudgetLineModalOpen(false)
    setEditingBudgetLine(null)
  }

  const handleDeleteLine = async (lineId: string) => {
    if (!window.confirm('Supprimer cette ligne budgétaire ?')) return
    try {
      await deleteBudgetLine.mutateAsync(lineId)
    } catch (error) {
      console.error('Error deleting budget line:', error)
    }
  }

  const handleConfirmDelete = async () => {
    if (!budgetId) return
    try {
      await deleteBudget.mutateAsync(budgetId)
      onClose()
    } catch (error) {
      console.error('Error deleting budget:', error)
    }
  }

  const formatCurrencyShort = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const handleVoteClick = () => {
    if (!budget) return

    // Validation spécifique pour les budgets fonds travaux ALUR (reserve)
    if (budget.budget_type === 'reserve') {
      const budgetTotal = budget.total_amount ?? 0

      // Vérifier qu'il y a un budget charges courantes
      if (operatingTotal === 0) {
        toaster.create({
          title: 'Validation impossible',
          description: 'Vous devez d\'abord créer un budget charges courantes pour valider le fonds travaux ALUR.',
          type: 'error',
        })
        return
      }

      // Vérifier que le montant atteint le minimum de 5%
      if (budgetTotal < minimumALUR) {
        toaster.create({
          title: 'Montant insuffisant',
          description: `Le fonds travaux ALUR doit représenter au minimum 5% du budget charges courantes (${formatCurrencyShort(minimumALUR)}). Montant actuel : ${formatCurrencyShort(budgetTotal)}.`,
          type: 'error',
        })
        return
      }
    }

    setShowVoteConfirm(true)
  }

  const handleConfirmVote = async () => {
    if (!budgetId) return
    try {
      await voteBudget.mutateAsync(budgetId)
    } catch (error) {
      console.error('Error voting budget:', error)
    }
  }

  const handleInteractOutside = useCallback((e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    const target = e.detail.originalEvent.target as HTMLElement
    const tableRow = target.closest('[data-budget-row]') as HTMLElement

    if (tableRow) {
      return
    }

    onClose()
  }, [onClose])

  const formatCurrency = (value: number | null) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const getStatusBadge = (status: BudgetStatus) => {
    const config: Record<BudgetStatus, { color: string; label: string }> = {
      draft: { color: 'gray', label: 'Brouillon' },
      voted: { color: 'blue', label: 'Voté' },
      active: { color: 'green', label: 'Actif' },
      closed: { color: 'purple', label: 'Clôturé' },
    }
    const { color, label } = config[status]
    return (
      <Badge colorPalette={color} variant="subtle">
        {label}
      </Badge>
    )
  }

  const getBudgetTypeLabel = (type: string) => {
    return BUDGET_TYPE_LABELS[type as keyof typeof BUDGET_TYPE_LABELS] || type
  }

  // Calculate statistics
  const totalBudgeted = budget?.total_amount ?? 0
  const totalLines = budgetLines?.length ?? 0
  const linesTotal = budgetLines?.reduce((sum, line) => sum + (line.budgeted_amount ?? 0), 0) ?? 0

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="900px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4}>
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation arrows */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {budget && (
                  <>
                    <HStack gap={2} mb={1}>
                      <Badge variant="outline" size="sm">
                        {getBudgetTypeLabel(budget.budget_type)}
                      </Badge>
                      {getStatusBadge(budget.status)}
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {budget.name}
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      {budget.fiscal_year?.name}
                    </Text>
                  </>
                )}
                {!budget && <Heading size="md">Détails du budget</Heading>}
              </Box>

              {/* Actions */}
              <Box display="flex" gap={2} flexShrink={0}>
                <MenuRoot>
                  <MenuTrigger asChild>
                    <IconButton variant="ghost" size="sm" aria-label="Actions">
                      <MoreVertical size={18} />
                    </IconButton>
                  </MenuTrigger>
                  <Portal>
                    <MenuPositioner>
                      <MenuContent minW="200px">
                        <MenuItem value="add_line" onClick={handleOpenAddLine}>
                          <HStack gap={2}>
                            <Plus size={16} />
                            <Text>Ajouter un poste de coût</Text>
                          </HStack>
                        </MenuItem>
                        {budget?.status === 'draft' && (
                          <>
                            <MenuItem value="vote" onClick={handleVoteClick}>
                              <HStack gap={2}>
                                <CheckCircle size={16} />
                                <Text>Marquer comme voté</Text>
                              </HStack>
                            </MenuItem>
                            <MenuItem
                              value="delete"
                              onClick={() => setShowDeleteConfirm(true)}
                              color="red.600"
                              _hover={{ bg: 'red.50' }}
                            >
                              <HStack gap={2}>
                                <Trash2 size={16} />
                                <Text>Supprimer le budget</Text>
                              </HStack>
                            </MenuItem>
                          </>
                        )}
                      </MenuContent>
                    </MenuPositioner>
                  </Portal>
                </MenuRoot>
                <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                  <X size={18} />
                </IconButton>
              </Box>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Budget non trouvé" />}

            {budget && (
              <VStack align="stretch" gap={0} flex={1} height="100%">
                {/* Timestamps */}
                <HStack
                  gap={3}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="180px">
                    <Box color="blue.500">
                      <CalendarClock size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Créé le
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {formatDate(budget.created_at)}
                      </Text>
                    </VStack>
                  </HStack>

                  {budget.voted_at && (
                    <HStack gap={2} flex={1} minW="180px">
                      <Box color="green.500">
                        <CheckCircle size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          Voté en AG le
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {formatDate(budget.voted_at)}
                        </Text>
                      </VStack>
                    </HStack>
                  )}
                </HStack>

                {/* Stats Cards */}
                <Box px={6} pt={4}>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <Wallet size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Montant total
                            </Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                            {formatCurrency(totalBudgeted)}
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <FileText size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Lignes budgétaires
                            </Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold">
                            {totalLines}
                          </Text>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    <Card.Root size="sm" variant="outline">
                      <Card.Body>
                        <VStack align="flex-start" gap={1}>
                          <HStack gap={2}>
                            <TrendingUp size={16} />
                            <Text fontSize="xs" color="gray.600">
                              Total lignes
                            </Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold">
                            {formatCurrency(linesTotal)}
                          </Text>
                          {Math.abs(linesTotal - totalBudgeted) > 0.01 && (
                            <Text fontSize="xs" color="orange.500">
                              Écart: {formatCurrency(linesTotal - totalBudgeted)}
                            </Text>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </Grid>
                </Box>

                {/* Description */}
                {budget.description && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Body>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Description
                        </Text>
                        <Text>{budget.description}</Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Budget Lines */}
                <Box px={6} pt={4} pb={6} flex={1} overflow="auto">
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between" w="full">
                        <Heading size="sm">Détail des lignes budgétaires</Heading>
                        <Button size="sm" variant="outline" onClick={handleOpenAddLine}>
                          <Plus size={16} />
                          Ajouter un poste
                        </Button>
                      </HStack>
                    </Card.Header>
                    <Card.Body p={0}>
                      {budgetLines && budgetLines.length > 0 ? (
                        <Table.Root size="sm">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>Libellé</Table.ColumnHeader>
                              <Table.ColumnHeader>Clé de répartition</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">Montant</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">%</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {budgetLines.map((line) => (
                              <Table.Row key={line.id}>
                                <Table.Cell>
                                  <Text fontSize="sm" fontWeight="medium">{line.label || '-'}</Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm">
                                    {line.distribution_key?.name || 'Tantièmes généraux'}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                  <Text fontSize="sm" fontWeight="medium">
                                    {formatCurrency(line.budgeted_amount)}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                  <Text fontSize="sm" color="gray.600">
                                    {totalBudgeted > 0
                                      ? ((line.budgeted_amount ?? 0) / totalBudgeted * 100).toFixed(1)
                                      : 0}%
                                  </Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <HStack justify="flex-end" gap={1}>
                                    <MenuRoot>
                                      <MenuTrigger asChild>
                                        <IconButton
                                          variant="ghost"
                                          size="xs"
                                          aria-label="Actions"
                                        >
                                          <MoreVertical size={14} />
                                        </IconButton>
                                      </MenuTrigger>
                                      <Portal>
                                        <MenuPositioner>
                                          <MenuContent minW="150px">
                                            <MenuItem
                                              value="edit"
                                              onClick={() => handleOpenEditLine(line)}
                                            >
                                              <HStack gap={2}>
                                                <Edit size={14} />
                                                <Text>Modifier</Text>
                                              </HStack>
                                            </MenuItem>
                                            <MenuItem
                                              value="delete"
                                              onClick={() => handleDeleteLine(line.id)}
                                              color="red.600"
                                              _hover={{ bg: 'red.50' }}
                                            >
                                              <HStack gap={2}>
                                                <Trash2 size={14} />
                                                <Text>Supprimer</Text>
                                              </HStack>
                                            </MenuItem>
                                          </MenuContent>
                                        </MenuPositioner>
                                      </Portal>
                                    </MenuRoot>
                                  </HStack>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      ) : (
                        <Box textAlign="center" py={8} color="gray.500">
                          <Text>Aucune ligne budgétaire</Text>
                          <Button
                            mt={4}
                            size="sm"
                            variant="outline"
                            onClick={handleOpenAddLine}
                          >
                            <Plus size={16} />
                            Ajouter un poste de coût
                          </Button>
                        </Box>
                      )}
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>

      {/* Budget Line Modal */}
      {budget && (
        <BudgetLineModal
          isOpen={budgetLineModalOpen}
          onClose={handleCloseLineModal}
          budgetId={budget.id}
          residenceId={budget.residence_id}
          budgetLine={editingBudgetLine}
        />
      )}

      {/* Vote Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showVoteConfirm}
        onClose={() => setShowVoteConfirm(false)}
        onConfirm={handleConfirmVote}
        title="Marquer comme voté"
        message={`Confirmez-vous que le budget "${budget?.name}" a été voté en Assemblée Générale ?`}
        confirmLabel="Confirmer le vote"
        colorPalette="blue"
        icon={<Vote size={24} />}
        isLoading={voteBudget.isPending}
      />

      {/* Delete Confirmation Dialog */}
      {budget && (
        <ConfirmDeleteDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
          entityName={budget.name}
          entityType="budget"
        />
      )}
    </Drawer.Root>
  )
}
